import { motion, useScroll, useTransform, Variants } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveLocalizedText } from '@/helpers/resolveLocalizedText';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { TDR } from 'tdr-common';
import { useSearch } from '../../api/availability/search';
import { useTableWithPolicies } from '../../api/getTable';
import { useTimeslot } from '../../api/getTimeslot';
import { useModificationFlowState } from '../../context/ModificationFlowProvider';
import { useViewToggle } from '../../context/ViewToggleProvider';
import { useScreenSize } from '../../hooks/useScreenSize';
import { SearchFilters } from '../../routes/AvailabilityGuard';
import { ModifyBookingFormData } from '../../routes/BookingGuard';
import { SquareIconButton } from '../buttons/SquareIconButton';
import { DiscardChanges } from '../dialog/controlled-dialog/DiscardChanges';
import MediaCarousel from '../media-carousel/MediaCarousel';
import PolicySection from '../policies/PolicySection';
import { ReviewFooter } from '../search/ReviewFooter';
import { Skeleton } from '../skeleton/Skeleton';
import styles from './TableDrawer.module.scss';
import { TableDrawerTags } from './TableDrawerTags';
import ScrollToTop from '../scroll-to-top/scroll-to-top';
import { TableDrawerHeader } from './TableDrawerHeader';

const MEDIA_HEIGHT = 374;
const MEDIA_WIDTH = 400;

type TableDrawerModsProps = {
	selectedTable: TDR.Table;
	restaurant: TDR.Restaurant;
	reservationId: string;
	searchFilters: SearchFilters;
};

const TableDrawerMods = ({ selectedTable, restaurant, reservationId, searchFilters }: TableDrawerModsProps) => {
	const { i18n } = useTranslation();
	const { data: tableWithPolicies } = useTableWithPolicies({
		id: selectedTable.id,
		searchParams: searchFilters,
		location: 'table'
	});

	const { results, isLoading } = useSearch({
		params: searchFilters,
		restaurantId: restaurant.id,
		space: [selectedTable]
	});

	const navigate = useNavigate();

	const { data } = useTimeslot(selectedTable?.id, searchFilters.date, restaurant.id, searchFilters.time, searchFilters.groupSize);
	const { isSmallScreen } = useScreenSize();
	const { isVirtualView } = useViewToggle();

	const { setNewInvoice } = useModificationFlowState();

	const scrollContentRef = useRef<HTMLDivElement>(null);

	const methods = useFormContext<ModifyBookingFormData>();

	const timeslot = data?.timeSlot;
	const displayUnit = timeslot?.isPerGuest ? 'guest' : `${selectedTable.label ?? 'table'}`;

	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [isMounted, setIsMounted] = useState(false);

	const coverImagePath = selectedTable?.carouselImagePath;
	let mediaItems = selectedTable?.media || [];

	if (isSmallScreen) {
		mediaItems = [{ type: TDR.MediaType.IMAGE, path: coverImagePath } as TDR.MediaContent, ...mediaItems];
	}
	else if (mediaItems.length === 0) {
		mediaItems = [{ type: TDR.MediaType.IMAGE, path: coverImagePath } as TDR.MediaContent];
	}
	const conflictingFilter = results?.resultMap?.[selectedTable.id]?.conflict?.[0] || undefined;

	const resetFormAndExit = () => {
		methods.reset();
		setNewInvoice(undefined);
		navigate(`/booking/${reservationId}`);
	};

	const handleExitClick = () => {
		if (methods.formState.isDirty) {
			setIsConfirmOpen(true);
		}
		else {
			resetFormAndExit();
		}
	};

	const handleConfirmExit = () => {
		resetFormAndExit();
		setIsConfirmOpen(false);
	};

	const handleCancelExit = () => {
		setIsConfirmOpen(false);
	};

	const mediaVariants: Variants = {
		visible: {
			height: `${MEDIA_HEIGHT}px`,
			minHeight: `${MEDIA_HEIGHT}px`,
			transition: {
				delay: 0.4,
				duration: 0.3
			}
		},
		hidden: {
			height: '100vh',
			transition: {
				duration: 0.5
			}
		}
	};

	const slideAndFade: Variants = {
		visible: {
			y: 0,
			opacity: 1,
			transition: {
				duration: 0.5,
				delay: 0.4
			}
		},
		hidden: {
			y: '100%',
			opacity: 0,
			transition: {
				duration: 0.5
			}
		}
	};

	const containerVariants: Variants = {
		visible: {
			y: 0,
			opacity: 1
		},
		hidden: {
			y: '100%',
			opacity: 0
		}
	};

	const { scrollY } = useScroll({
		container: scrollContentRef
	});

	const mediaGalleryOpacity = useTransform(scrollY, [200, MEDIA_HEIGHT], [1, 0]);

	// For determining initial animation states of <motion /> elements
	//  - if component has not mounted yet, set initial to 'visible' to cancel animations
	//     (these animations are only used within the already mounted component, depending on isVirtualView state)
	useEffect(() => {
		setIsMounted(true);
	}, []);

	return (
		<>
			<motion.article
				className="relative bg-tablz-dark-gray-1 h-full sm:w-[400px] flex flex-col"
				variants={containerVariants}
				initial='visible'
				animate='visible'
				exit={'hidden'}
				transition={{ duration: 0.2 }}
			>
				{/* make sure we always land here scrolled to top */}
				<ScrollToTop />

				<div className="absolute top-[18px] left-3 z-10">
					<SquareIconButton variant='back' onClick={() => navigate(-1)} />
				</div>
				<div className="absolute top-[18px] right-[14px] z-10">
					<SquareIconButton variant='exit' onClick={handleExitClick} />
				</div>

				<div className="h-full overflow-y-scroll" ref={scrollContentRef}>
					<motion.div
						initial={isMounted ? 'hidden' : 'visible'}
						style={{ opacity: mediaGalleryOpacity }}
						animate={isVirtualView ? 'hidden' : 'visible'}
						variants={mediaVariants}
					>
						{!coverImagePath ? (
							<Skeleton height='270px' width='100%' />
						) : (
							<MediaCarousel
								variant={'default'}
								tableLabel={selectedTable.label}
								itemHeight={MEDIA_HEIGHT}
								itemWidth={MEDIA_WIDTH}
								items={mediaItems}
								brightness={restaurant.brightness}
								contrast={restaurant.contrast}
							/>
						)}
					</motion.div>

					<motion.div
						variants={slideAndFade}
						animate={isVirtualView ? 'hidden' : 'visible'}
						className="px-2 py-3 flex flex-col gap-2"
					>
						<TableDrawerHeader table={selectedTable} />
						<div className="px-2 font-poppins text-base text-zinc-300 pb-3" tabIndex={0}>
							{resolveLocalizedText(tableWithPolicies?.description ?? selectedTable?.description, i18n.language)}
						</div>
						<TableDrawerTags table={selectedTable} />
						{tableWithPolicies?.policies?.length > 0 ? (
							<div className={styles.Policies} tabIndex={0}>
								<PolicySection table={tableWithPolicies} />
							</div>
						) : null}
					</motion.div>
				</div>

				<motion.footer
					className="w-full bg-tablz-dark-gray-2 border-t border-tablz-med-gray-4 py-3 px-2 flex justify-center items-center"
					initial={isMounted ? 'hidden' : 'visible'}
					animate={isVirtualView ? 'hidden' : 'visible'}
					variants={slideAndFade}
				>
					{isLoading ? (
						<Skeleton width='100%' height='56px' />
					) : (
						<ReviewFooter
							priceAmount={timeslot?.regularPrice}
							displayUnit={displayUnit}
							selectedTable={selectedTable}
							restaurant={restaurant}
							conflictingFilter={conflictingFilter}
						/>
					)}
				</motion.footer>
			</motion.article>
			<DiscardChanges
				open={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				onDiscard={handleConfirmExit}
				onCancel={handleCancelExit}
			/>
		</>
	);
};

export default TableDrawerMods;
